/**
 * Stable API error codes returned by the backend (narrow to a union over time).
 */
export type FlashkaApiErrorCode = string;

/**
 * A 4xx the API can explain: `error` is the sentence for the person, `reason`
 * the stable code for code and for Sentry. A client attaches `reason` to the
 * error it reports, so one refusal is told apart from another without the
 * server's log (FLASHKA-VITE-209: a 403 whose cause lived only in Vercel).
 */
export type ApiRefusalBody<R extends string = FlashkaApiErrorCode> = {
  error: string;
  reason: R;
};

/**
 * Why `POST /transcribe` would not start.
 * - `owner_mismatch` — `filePath` is outside the caller's own prefix;
 * - `unsafe_key` — under the caller's prefix, but not a plain key (`..`, `//`, …);
 * - `both_inputs` / `missing_input` — neither or both of `filePath` and `segments`;
 * - `missing_session` / `missing_mime` — a live recording without its session or type;
 * - `too_many_segments` / `invalid_segment` / `too_long` — a segment list over the limits;
 * - `session_unresolved` (410) — a key no session can be derived from;
 * - `segments_missing` (409) — a declared part is not in storage yet.
 */
export type TranscribeRefusalReason =
  | 'owner_mismatch'
  | 'unsafe_key'
  | 'both_inputs'
  | 'missing_input'
  | 'missing_session'
  | 'missing_mime'
  | 'too_many_segments'
  | 'invalid_segment'
  | 'too_long'
  | 'session_unresolved'
  | 'segments_missing';

/** Codes are snake_case words; anything else in `reason` is not one of ours. */
const REASON = /^[a-z][a-z0-9_]{0,63}$/;

/** `body.reason` when the body is an API refusal carrying one. */
export function apiRefusalReason(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const { reason } = body as { reason?: unknown };
  return typeof reason === 'string' && REASON.test(reason) ? reason : undefined;
}
